import { OrgEventStreamContext } from "@/contexts/OrgEventStreamContext";
import { createOrgEventDispatcher } from "@/hooks/dispatch-org-event";
import { OrgEventStreamManager } from "@/hooks/org-event-stream-manager";
import { useFlagScopedEventStreamsEnabled } from "@/hooks/use-flag";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, type PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";

/**
 * Provides the org-scoped event stream to the component tree.
 *
 * This lives at the root route level because we don't yet have a single
 * AuthenticatedUserLayout instantiation that could own the stream.
 * See AGENT-477.
 *
 * When the scoped_event_streams_enabled flag is OFF, falls back to the
 * legacy unfiltered stream via useStreamEvents.
 *
 * When the flag is ON, creates an OrgEventStreamManager that merges
 * interests from useEventInterest hooks into filtered WatchEvents calls.
 */
export const OrgEventStreamProvider: FC<PropsWithChildren> = ({ children }) => {
    const { value: isScopedEnabled, loading } = useFlagScopedEventStreamsEnabled();

    // Wait for the flag to resolve before connecting any stream.
    // Without this gate, the legacy stream would connect first and then
    // get torn down once the flag resolves to true — wasting a connection
    // and risking a brief event gap.
    if (loading) {
        return <OrgEventStreamContext.Provider value={null}>{children}</OrgEventStreamContext.Provider>;
    }

    return isScopedEnabled ? (
        <ScopedEventStream>{children}</ScopedEventStream>
    ) : (
        <LegacyEventStream>{children}</LegacyEventStream>
    );
};

/** Legacy path: unfiltered org-scoped stream, no manager context. */
const LegacyEventStream: FC<PropsWithChildren> = ({ children }) => {
    const { unsubscribe } = useStreamEvents({ case: "organization", value: true });

    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            unsubscribe();
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal, unsubscribe]);

    return <OrgEventStreamContext.Provider value={null}>{children}</OrgEventStreamContext.Provider>;
};

/** Scoped path: manager-driven filtered stream. */
const ScopedEventStream: FC<PropsWithChildren> = ({ children }) => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    // Track the dispatcher ref so we can clean up timers on principal change.
    const dispatcherRef = useRef<ReturnType<typeof createOrgEventDispatcher> | null>(null);

    const manager = useMemo(() => {
        if (!user) {
            return null;
        }

        // Clean up previous dispatcher's debounce timers
        dispatcherRef.current?.cleanup();

        const dispatcher = createOrgEventDispatcher(user.organizationId);
        dispatcherRef.current = dispatcher;

        return new OrgEventStreamManager(api, client, dispatcher.dispatch);
        // Recreate when user/org changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, client, user?.id, user?.organizationId]);

    useEffect(() => {
        return () => {
            dispatcherRef.current?.cleanup();
            manager?.dispose();
        };
    }, [manager]);

    return <OrgEventStreamContext.Provider value={manager}>{children}</OrgEventStreamContext.Provider>;
};
